import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";

import { IdLinkCell } from "~/shared/components/trax-grid/id-link-cell/id-link-cell.tsx";
import { LoaderCell } from "~/shared/components/trax-grid/loader-cell/loader-cell.tsx";
import type { Load } from "~/shared/schemas/loads-schema.ts";

import { useLoadCellContext } from "../../utils/load-cell-context.tsx";
import * as styles from "./po-number-cell.module.css";

/**
 * Stable module-level cell renderer for the TQL PO # column. Reads runtime
 * config (isLoading, isInternal, callbacks) from `LoadCellContext` so its
 * function reference stays identical across renders — preventing Kendo from
 * unmounting and remounting every cell when column prefs change.
 *
 * Internal users navigate to Shipment Management; external users open the
 * details flyout (when the feature flag is enabled) or see plain text.
 * The internal link only requires a PO number — shipments created outside
 * TRAX (e.g. API/EDI tenders) may have no quote ID but must still open.
 */
export const PoNumberCell = (props: GridCustomCellProps) => {
	const { isLoading, isInternal, detailsFlyoutEnabled, onDetailsClick } =
		useLoadCellContext();
	const { poNumber } = props.dataItem as { poNumber?: string };

	if (isLoading) return <LoaderCell {...props} />;

	if (isInternal) {
		return (
			<IdLinkCell
				displayText={poNumber ?? ""}
				isDisabled={!poNumber}
				linkTo={`/shipment-management/${poNumber ?? ""}`}
				{...props}
			/>
		);
	}

	if (detailsFlyoutEnabled) {
		return (
			<IdLinkCell
				displayText={poNumber ?? ""}
				isDisabled={!poNumber}
				linkTo={"#"}
				onClick={() => {
					onDetailsClick?.(props.dataItem as Load);
				}}
				{...props}
				className={styles.poLink}
			/>
		);
	}

	return <div {...props.tdProps}>{poNumber}</div>;
};
